import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, RefreshCw, Brain, ArrowLeft } from "lucide-react";

interface KnowledgeSection {
  id: string;
  section_name: string;
  content: string;
  is_active: boolean;
  display_order: number;
}

const AdminAIKnowledge = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sections, setSections] = useState<KnowledgeSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/admin/login");
      return;
    }
    setUser(session.user);
    fetchKnowledgeBase();
  };

  const fetchKnowledgeBase = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("ai_knowledge_base")
        .select("*")
        .order("display_order");

      if (error) throw error;
      setSections(data || []);
    } catch (error) {
      console.error("Error fetching knowledge base:", error);
      toast({
        title: "Error",
        description: "Failed to load AI knowledge base",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSection = (id: string, field: keyof KnowledgeSection, value: any) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, [field]: value } : section
    ));
  };

  const saveKnowledgeBase = async () => {
    try {
      setSaving(true);

      // Update each section
      for (const section of sections) {
        const { error } = await supabase
          .from("ai_knowledge_base")
          .update({
            content: section.content,
            is_active: section.is_active,
            display_order: section.display_order,
          })
          .eq("id", section.id);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "AI knowledge base updated successfully. Changes will be reflected in new conversations.",
      });
    } catch (error) {
      console.error("Error saving knowledge base:", error);
      toast({
        title: "Error",
        description: "Failed to save AI knowledge base",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getSectionTitle = (sectionName: string) => {
    return sectionName
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getSectionDescription = (sectionName: string) => {
    const descriptions: Record<string, string> = {
      company_overview: "Basic information about Rick's Black Car Service, fleet, and service areas",
      services: "Detailed service descriptions including airport transfers, corporate travel, special events, etc.",
      pricing: "Pricing structure, base fees, popular routes, and special rates",
      fleet: "Vehicle specifications and amenities",
      booking_instructions: "Critical instructions for how Denise should handle booking requests and quotes",
      why_choose_ricks: "Key differentiators and value propositions",
      conversation_style: "Guidelines for how Denise should communicate with customers",
    };
    return descriptions[sectionName] || "";
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/dashboard")}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-primary/10">
                <Brain className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">AI Knowledge Base</h1>
                <p className="text-muted-foreground">
                  Manage what Denise knows about your business
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={fetchKnowledgeBase}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={saveKnowledgeBase}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save All Changes
                </>
              )}
            </Button>
          </div>
        </div>

        <Card className="bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="text-blue-600 dark:text-blue-400">
                <Brain className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  How This Works
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  These sections define what Denise (your AI assistant) knows about your business. 
                  Update the content below to teach Denise new information. Changes take effect 
                  immediately for all new conversations. Active sections are used in AI responses.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {sections.map((section) => (
            <Card key={section.id} className="card-luxury">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-xl">
                      {getSectionTitle(section.section_name)}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {getSectionDescription(section.section_name)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${section.id}`} className="text-sm">
                        Active
                      </Label>
                      <Switch
                        id={`active-${section.id}`}
                        checked={section.is_active}
                        onCheckedChange={(checked) =>
                          updateSection(section.id, "is_active", checked)
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`order-${section.id}`} className="text-sm">
                        Order
                      </Label>
                      <Input
                        id={`order-${section.id}`}
                        type="number"
                        min="0"
                        value={section.display_order}
                        onChange={(e) =>
                          updateSection(
                            section.id,
                            "display_order",
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="w-20"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor={`content-${section.id}`}>Content</Label>
                  <Textarea
                    id={`content-${section.id}`}
                    value={section.content}
                    onChange={(e) =>
                      updateSection(section.id, "content", e.target.value)
                    }
                    rows={10}
                    className="font-mono text-sm"
                    placeholder="Enter the knowledge content for this section..."
                  />
                  <p className="text-xs text-muted-foreground">
                    {section.content.length} characters
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-end pt-4">
          <Button
            onClick={saveKnowledgeBase}
            disabled={saving}
            size="lg"
            className="gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving All Changes...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save All Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAIKnowledge;
